import React from "react";
import PropTypes from "prop-types";
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogContentText,
	DialogActions,
	Button
} from "@mui/material";
import useApi from "../hooks/useApi";

/**
 * EnvironmentDeleteModal component renders a modal dialog for confirming
 * the deletion of an environment.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {Object} props.config - The configuration object containing URIs.
 * @param {boolean} props.isOpen - Flag to determine if the modal is open.
 * @param {Function} props.onChange - Function to handle open state changes.
 * @param {Function} props.onChangeBulk - Function to handle bulk state changes.
 * @param {(string|number)} props.applicationId - The application ID.
 * @param {string} props.environmentName - The environment name.
 * @param {(string|number)} props.environmentId - The environment ID.
 *
 * @return {JSX.Element} The rendered component.
 */
const EnvironmentDeleteModal = (props) => {
	const {
		config,
		isOpen,
		onChange,
		onChangeBulk,
		applicationId,
		environmentName,
		environmentId
	} = props;

	const api = useApi();

	/**
	 * Closes the modal dialog by setting its open state to false.
	 *
	 * @function handleClose
	 */
	const handleClose = () => {
		onChange(false, "environmentDeleteModalIsOpen");
	};

	/**
	 * Handles the deletion of the environment by calling the delete and search methods from useApi.
	 *
	 * @async
	 * @function handleSubmit
	 */
	const handleSubmit = async () => {
		await api.environment.delete({ id: environmentId });
		const environments = await api.environment.search({ queryParams: { applicationId: applicationId } });
		onChangeBulk({
			environmentDeleteModalIsOpen: false,
			environments: environments.rows
		});
	};

	return (
		<Dialog open={isOpen} onClose={handleClose}>
			<DialogTitle>
				Delete "{environmentName}" Environment?
			</DialogTitle>
			<DialogContent>
				<DialogContentText>
					WARNING: Deleting an environment will cause any network traffic to fail for the environment as well as prevent application startup. Are you sure?
				</DialogContentText>
			</DialogContent>
			<DialogActions>
				<Button onClick={handleClose} color="primary">
					Cancel
				</Button>
				<Button onClick={handleSubmit} color="secondary" autoFocus>
					DELETE
				</Button>
			</DialogActions>
		</Dialog>
	);
};

EnvironmentDeleteModal.propTypes = {
	config: PropTypes.shape({
		uri: PropTypes.shape({
			base: PropTypes.string.isRequired
		}).isRequired
	}).isRequired,
	isOpen: PropTypes.bool.isRequired,
	onChange: PropTypes.func.isRequired,
	onChangeBulk: PropTypes.func.isRequired,
	applicationId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
	environmentName: PropTypes.string.isRequired,
	environmentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired
};

export default EnvironmentDeleteModal;
